import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Routes, ROUTE_ICONS, UI_ROUTES, EXTERNAL_ROUTES_PROPS, type RouteIconKeys } from 'src/constants/routes';
import { UseMenuToggleResult } from '../hooks';
import {
  StyledList,
  StyledListItem,
  StyledNavLink,
  StyledNavLinkLabel,
  StyledNavLinkIcon,
  StyledExternalNavLink,
} from './Links.styled';
import { NavBarComponentsCommonProps } from '../common';
import { CollapsibleLinkItem } from './CollapsibleLinkItem';

interface IDashboardLinksFormat {
  path?: (typeof UI_ROUTES)[Routes];
  label: string;
  search?: string;
  external_url?: string;
  sub_routes?: IDashboardLinksFormat[];
  icon_key?: RouteIconKeys;
}

interface DashboardLinks {
  dashboardLinks: IDashboardLinksFormat[];
}

export type LinksProps = DashboardLinks &
  Omit<NavBarComponentsCommonProps, 'toggleMenu'> &
  Pick<UseMenuToggleResult, 'handleCloseMobileMenu'>;

type LinkComponentProps = LinksProps['dashboardLinks'][number] &
  Pick<UseMenuToggleResult, 'handleCloseMobileMenu'> & {
    menuIsOpen: boolean;
  };

const OVERVIEW_PATHS = [UI_ROUTES.DEVICE_MANAGEMENT, UI_ROUTES.REPORT];

const LinkComponent = ({
  path,
  search,
  label,
  menuIsOpen,
  handleCloseMobileMenu,
  external_url = undefined,
  icon_key = undefined,
}: LinkComponentProps) => {
  const { t } = useTranslation();

  if (external_url) {
    const ExternalIcon = icon_key ? ROUTE_ICONS[icon_key] : null;
    return (
      <StyledListItem>
        <StyledExternalNavLink href={external_url} onClick={handleCloseMobileMenu} {...EXTERNAL_ROUTES_PROPS}>
          <StyledNavLinkIcon>{ExternalIcon ? <ExternalIcon /> : null}</StyledNavLinkIcon>
          <StyledNavLinkLabel $menuIsOpen={menuIsOpen}>{t(label)}</StyledNavLinkLabel>
        </StyledExternalNavLink>
      </StyledListItem>
    );
  }
  if (typeof path !== 'string') return null;

  const Icon = ROUTE_ICONS[path];

  // Workaround for overview path active state
  const isOverviewPath = OVERVIEW_PATHS.includes(path);
  const isOverviewPathActive = OVERVIEW_PATHS.includes(window.location.pathname);

  return (
    <StyledListItem key={`${path}${label}`}>
      <StyledNavLink
        to={{
          pathname: path,
          search: search || '',
        }}
        onClick={handleCloseMobileMenu}
        $isOverviewPathActive={isOverviewPathActive}
        $isOverviewPath={isOverviewPath}
      >
        <StyledNavLinkIcon>{Icon ? <Icon /> : null}</StyledNavLinkIcon>
        <StyledNavLinkLabel $menuIsOpen={menuIsOpen}>{t(label)}</StyledNavLinkLabel>
      </StyledNavLink>
    </StyledListItem>
  );
};

export const Links: React.FunctionComponent<LinksProps> = ({ menuIsOpen, dashboardLinks, handleCloseMobileMenu }) => {
  return (
    <StyledList>
      {dashboardLinks.map(({ path, search, label, sub_routes, icon_key, external_url }) => {
        if (Array.isArray(sub_routes) && sub_routes.length > 0) {
          return (
            <CollapsibleLinkItem key={`${icon_key}${label}`} label={label} $menuIsOpen={menuIsOpen} icon_key={icon_key}>
              <Links
                dashboardLinks={sub_routes}
                menuIsOpen={menuIsOpen}
                handleCloseMobileMenu={handleCloseMobileMenu}
              />
            </CollapsibleLinkItem>
          );
        }

        return (
          <LinkComponent
            key={`${path ?? external_url}${label}`}
            path={path}
            search={search}
            label={label}
            menuIsOpen={menuIsOpen}
            handleCloseMobileMenu={handleCloseMobileMenu}
            external_url={external_url}
            icon_key={icon_key}
          />
        );
      })}
    </StyledList>
  );
};
